import type { RelationshipEvidenceResponse, RelationshipGraphData, RelationshipGroup, RelationshipGroupDetail, RelationshipPage, RelationshipPair, RelationshipPairDetail, RelationshipPlayer, RelationshipPlayerDetail } from '../types/relationships';

const BASE = import.meta.env.DEV ? 'https://spy.playcdu.co/api/notes/relationships' : '/api/notes/relationships';

export class RelationshipsApiError extends Error {
  constructor(public status: number, message: string) { super(message); this.name = 'RelationshipsApiError'; }
}

const messages: Record<number, string> = {
  401: 'Your session has expired. Sign in to Argus again.',
  403: 'Your account needs the argus.identaware permission to view relationships.',
  404: 'This record is not in the retained analysis. Refresh to see current evidence.',
  409: 'The analysis changed while you were exploring. Refresh to load the current groups and evidence together.',
  429: 'Relationships is busy. Please try again shortly.',
  502: 'The relationship service could not be reached. Please retry.',
  503: 'Relationships is not ready. The service may be disconnected or awaiting configuration.',
  504: 'The relationship service took too long to respond. Please retry.',
};

async function safeServiceMessage(response: Response): Promise<string | null> {
  if (!response.headers.get('content-type')?.includes('application/json') || !response.body) return null;
  if (Number(response.headers.get('content-length')) > 4096) { await response.body.cancel(); return null; }
  const reader = response.body.getReader(), decoder = new TextDecoder();
  let bytes = 0, text = '';
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      bytes += value.byteLength;
      if (bytes > 4096) { await reader.cancel(); return null; }
      text += decoder.decode(value, { stream: true });
    }
    const data: unknown = JSON.parse(text + decoder.decode());
    if (!data || typeof data !== 'object' || !('error' in data)) return null;
    if (data.error === 'read_model_building') return 'Completed analysis is being indexed for staff searches. Please retry shortly.';
    if (data.error === 'graph_unavailable') return 'Update IdentAware to enable the graph.';
    return null;
  } catch { return null; }
  finally { reader.releaseLock(); }
}

async function request<T>(path: string, params: Record<string, string | number | undefined>, signal?: AbortSignal): Promise<T> {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value !== undefined) search.set(key, String(value));
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  if (signal?.aborted) controller.abort();
  let timedOut = false;
  const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, 25_000);
  try {
    const response = await fetch(`${BASE}${path}?${search}`, {
      credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      const message = (response.status === 503 ? await safeServiceMessage(response) : null)
        || messages[response.status] || 'Relationships could not complete the request. Please retry.';
      throw new RelationshipsApiError(response.status, message);
    }
    if (response.redirected || !response.headers.get('content-type')?.includes('application/json')) {
      throw new RelationshipsApiError(401, messages[401]);
    }
    return await response.json() as T;
  } catch (error) {
    if (timedOut) throw new RelationshipsApiError(504, messages[504]);
    if (error instanceof RelationshipsApiError || controller.signal.aborted) throw error;
    throw new RelationshipsApiError(502, messages[502]);
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', abort);
  }
}

export const relationshipsApi = {
  graph: (options: { player?: string; group?: string; snapshot?: string; maxNodes?: number; maxEdges?: number } = {}, signal?: AbortSignal) => request<RelationshipGraphData>('/graph', { maxNodes: 80, maxEdges: 150, ...options }, signal),
  groups: (player?: string, offset = 0, snapshot?: string, signal?: AbortSignal) => request<RelationshipPage<RelationshipGroup>>('/groups', { player, offset, limit: 12, snapshot }, signal),
  group: (id: string, snapshot: string, signal?: AbortSignal) => request<RelationshipGroupDetail>(`/groups/${encodeURIComponent(id)}`, { snapshot }, signal),
  players: (q: string, signal?: AbortSignal) => request<RelationshipPage<RelationshipPlayer>>('/players', { q }, signal),
  player: (uuid: string, signal?: AbortSignal) => request<RelationshipPlayerDetail>(`/players/${encodeURIComponent(uuid)}`, {}, signal),
  pairs: (player: string, offset = 0, snapshot?: string, signal?: AbortSignal) => request<RelationshipPage<RelationshipPair>>('/pairs', { player, offset, limit: 10, snapshot }, signal),
  pair: (id: string, snapshot: string, signal?: AbortSignal) => request<RelationshipPairDetail>(`/pairs/${encodeURIComponent(id)}`, { snapshot }, signal),
  evidence: (id: string, snapshot: string, signal?: AbortSignal) => request<RelationshipEvidenceResponse>(`/evidence/${encodeURIComponent(id)}`, { snapshot }, signal),
};
